import { Patient, PatientDocument, PhysicianContact, ReferralRequest } from 'generated/api';
import _ from 'lodash';
import React, { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'careand-redux/store';
import PatientUtils from 'services/PatientUtils';
import { PatientImage } from 'components/Common/UserImage';
import ListWithDetails from 'components/Common/ListWithDetails';
import GenericBadge from 'components/Utils/GenericBadge';
import PhysicianBadge from 'components/Physician/PhysicianBadge';
import PatientBadge from 'components/Patient/PatientBadge';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import { api } from 'careand-redux/api/ApiThunks';
import './style.scss';
import DefaultModal from 'components/Utils/DefaultModal';
import NewReferralAdmin from 'components/HealthRecords/Documents/NewReferralAdmin';
import { DefaultCheckbox, DefaultSwitch } from 'services/UIUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import ProgressMessage from 'components/Utils/ProgressMessage';
import Icon from 'components/Utils/NewIcon';
import DefaultChip from 'components/Utils/DefaultChip';
import DocumentDetailsModal from 'components/HealthRecords/Documents/DocumentDetailsModal';
import useLockDocument from 'hooks/useLockDocument';
import usePersistedState, { useSafePersistedState } from '../../../../hooks/usePersistedState';
import { GetStaff, IsAdmin } from '../../../../careand-redux/selectors';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import {
  ContactRow,
  ReferralAttachmentModal,
} from '../../../../components/HealthRecords/Referrals/NewReferral';
import ConfirmationButton from '../../../../components/Utils/NewDefaultButton/ConfirmationButton';

interface ReferralRequestsProps {
  requests: ReferralRequest[];
  onClose: () => void;
}

const ReferralRequests: FC<ReferralRequestsProps> = ({ requests }) => {
  return (
    <ListWithDetails
      title="Referral Requests"
      items={requests}
      onClick={undefined}
      renderDetails={(request) => <ReferralRequestDetails request={request} />}
      renderBadge={(request) => <ReferralRequestBadge request={request} />}
    />
  );
};

interface ReferralRequestDetailsProps {
  request: ReferralRequest;
  onDeleted?: () => void;
  onClose?: () => void;
}

export const ReferralRequestDetails: FC<ReferralRequestDetailsProps> = ({
  request,
  onDeleted,
  onClose,
}) => {
  const patientId = request.patient_id;
  const dispatch = useAppDispatch();
  const [referralModal, setReferralModal] = useState<boolean>(false);
  const [documents, setDocuments] = useState<PatientDocument[] | undefined>(undefined);
  const [edit, setEdit] = useState<boolean>(false);
  const [openDocument, setOpenDocument] = useState<PatientDocument | undefined>();
  const ownId = useAppSelector((state) => state.authentication.user!.id);
  const isLoadingDocuments = useIsLoading(api.Documents.getDocument);
  const [lockedId, lockedName, lockLoading] = useLockDocument(request.id!, true);

  const isAdmin = useAppSelector(IsAdmin);

  useEffect(() => {
    if (!documents && request.document_ids && request.document_ids.length > 0) {
      Promise.all(
        request.document_ids.map(async (documentId) => {
          return dispatch(api.Documents.getDocument({ documentId }));
        }),
      ).then((documents) => {
        setDocuments(_.compact(documents) || []);
      });
    }
  }, [dispatch, request, documents, patientId]);

  const attachments = useMemo<PatientDocument[]>(() => {
    return _.compact(
      (request.document_ids || []).map((id) => documents?.find((doc) => doc?.id === id)),
    );
  }, [request, documents]);

  const patient = useAppSelector((state) => state.patients.byId[request.patient_id]);
  const physician = useAppSelector((state) => state.physicians.byId[request.physician_id]);

  const isDeleting = useIsLoading(api.HealthRecords.removeReferralRequest);

  async function onDelete() {
    await dispatch(api.HealthRecords.removeReferralRequest({ id: request.id! }), {
      errorTitle: 'Delete Referral Request',
    });
    if (onDeleted) onDeleted();
  }

  const modifiedRequest = useMemo(() => {
    return {
      ...request,
      attachments: attachments.map(({ filename }) => filename),
    };
  }, [attachments, request]);

  const isLocked = lockedId && lockedId !== ownId;

  if (edit) {
    return (
      <div
        className="details-panel padded-box referral-request-details scroll-list"
        style={{ width: '665px' }}
      >
        <NewReferralRequest patient={patient!} onSuccess={onClose} referralRequest={request} />
      </div>
    );
  }

  return (
    <div className="details-panel padded-box referral-request-details scroll-list">
      <span className="small-title">Requesting Practitioner</span>
      <PhysicianBadge physician={physician} />
      <span className="small-title">Patient</span>
      <PatientBadge patient={patient} goToProfile={true} />
      <div className="toggles">
        <DefaultCheckbox
          name="request_urgent"
          value={!!request.urgent}
          disabled={true}
          text="Urgent"
        />
        <DefaultCheckbox
          name="request_hide"
          value={!!request.hide}
          disabled={true}
          text="Hide from Patient"
        />
      </div>
      {request.instructions && (
        <>
          <span className="small-title">Practitioner Instructions</span>
          <pre>{request.instructions}</pre>
          {openDocument && (
            <DocumentDetailsModal
              isOpen={!!openDocument}
              document={openDocument}
              onClose={() => setOpenDocument(undefined)}
            />
          )}
        </>
      )}
      {attachments.length > 0 && (
        <>
          <span className="small-title">{attachments.length} attachment(s)</span>
          <div className="message-attachments">
            {attachments.map((document) => (
              <DefaultChip
                text={document.title}
                onClick={() => setOpenDocument(document)}
                backgroundColor="#2693d1"
                textColor="white"
                avatar={<Icon fontAwesome="paperclip" className="white" height="15px" />}
              />
            ))}
          </div>
        </>
      )}
      <div style={{ display: 'inline-flex' }}>
        <ProgressMessage show={!!lockLoading || isLoadingDocuments} />
      </div>
      <div className="actions">
        {isLocked && (
          <div className="locked">
            <Icon src="lock" />
            <span>This document being edited by {lockedName}</span>
          </div>
        )}
        {!isLocked && !lockLoading && !isLoadingDocuments && (
          <div className="flex flex-col gap-3 full-width">
            <DefaultButton
              name="Edit Referral Request"
              onClick={() => setEdit(true)}
              icon="pencil"
              style={{ width: '100%' }}
              hide={isAdmin}
            />
            <ConfirmationButton
              name="Delete Referral Request"
              onClick={onDelete}
              confirmation={{
                message: 'Are you sure you want to delete this request?',
              }}
              className="red"
              icon="trash"
              loading={isDeleting}
              style={{ width: '100%' }}
            />
            <DefaultButton
              name="Save Referral"
              onClick={() => setReferralModal(true)}
              className="blue"
              icon="plus"
              style={{ width: '100%' }}
            />
          </div>
        )}
      </div>
      {referralModal && (
        <DefaultModal
          wrap={true}
          title="New Referral"
          className="fit-modal new-referral-modal"
          padding={false}
          content={
            <NewReferralAdmin
              patient={patient}
              request={modifiedRequest}
              onClose={() => setReferralModal(false)}
              physician={physician}
            />
          }
          isOpen={referralModal}
          onRequestClose={() => setReferralModal(false)}
        />
      )}
    </div>
  );
};

interface ReferralRequestBadgeProps {
  request: ReferralRequest;
}

export const ReferralRequestBadge: FC<ReferralRequestBadgeProps> = ({ request }) => {
  const patient = useAppSelector((state) => state.patients.byId[request.patient_id]);

  return (
    <GenericBadge
      id={request.id}
      className="referral-request-badge"
      title={PatientUtils.getName(patient)!}
      avatar={<PatientImage user={patient} size={40} />}
      info={<span>{request.instructions}</span>}
    />
  );
};

const DEFAULT_REQUEST = {
  instructions: '',
  urgent: false,
  hide: false,
  document_ids: [] as string[],
  send_to: null as PhysicianContact | null,
};

interface NewReferralRequestProps {
  patient: Patient;
  onSuccess?: () => void;
  referralRequest?: ReferralRequest;
  resetStateOnMount?: boolean;
}

export const NewReferralRequest: FC<NewReferralRequestProps> = ({
  patient,
  onSuccess,
  referralRequest,
  resetStateOnMount,
}) => {
  const dispatch = useAppDispatch();
  const [value, setValue, deleteSetValue] = useSafePersistedState(
    `referralRequest-${patient.id}-${referralRequest?.id}`,
    referralRequest || DEFAULT_REQUEST,
  );
  const [attachmentModal, setAttachmentModal] = useState(false);
  const isLoading = useIsLoading(api.HealthRecords.addReferralRequest);
  const ownUser = useAppSelector(GetStaff);

  useEffect(() => {
    if (resetStateOnMount) {
      setValue(referralRequest || DEFAULT_REQUEST);
    }
  }, []);

  async function createRequest() {
    const request: ReferralRequest = {
      ...(referralRequest || {}),
      clinic_id: ownUser!.clinic_id!,
      physician_id: ownUser!.id!,
      patient_id: patient.id!,
      instructions: value.instructions,
      urgent: value.urgent,
      hide: value.hide,
      document_ids: value.document_ids,
      send_to: value.send_to || undefined,
    };

    let response = await dispatch(
      api.HealthRecords.addReferralRequest({ referralRequest: request }),
      {
        title: 'Referral Request',
        successText: 'Referral Request saved successfully!',
      },
    );
    if (response) {
      deleteSetValue();
      onSuccess?.();
    }
  }

  return (
    <div className="referral-request">
      <PatientBadge patient={patient} />
      <ContactRow
        patient={patient}
        contact={value.send_to}
        onSelect={(send_to: PhysicianContact) => setValue({ ...value, send_to })}
      />
      <div className="toggles">
        <DefaultButton
          name={`${(value.document_ids || []).length} attachments`}
          icon="paperclip"
          onClick={() => setAttachmentModal(true)}
          disabled={isLoading}
        />
        <DefaultSwitch
          onChange={(event: ChangeEvent<HTMLInputElement>, isInputChecked: boolean) =>
            setValue({ ...value, urgent: isInputChecked })
          }
          label="Urgent"
          padded={false}
          checked={value.urgent}
        />
        <DefaultSwitch
          onChange={(event: ChangeEvent<HTMLInputElement>, isInputChecked: boolean) =>
            setValue({ ...value, hide: isInputChecked })
          }
          label="Hide from Patient"
          padded={false}
          checked={value.hide}
        />
      </div>
      <ReferralAttachmentModal
        setAttachmentModal={setAttachmentModal}
        attachmentModal={attachmentModal}
        patient={patient}
        formAttachments={value.document_ids!}
        useIds={true}
        change={(key: string, document_ids: string[]) => {
          setValue({ ...value, document_ids });
        }}
      />
      <span className="small-title">Instructions for Admin</span>
      <TextareaAutosize
        cacheMeasurements
        name="instructions"
        value={value.instructions}
        minRows={15}
        onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
          setValue({ ...value, instructions: event.target.value })
        }
      />
      <DefaultButton
        name="Save Request"
        onClick={createRequest}
        loading={isLoading}
        className="blue"
      />
    </div>
  );
};

export default ReferralRequests;
